/**
 * Nighttime breastfeeding selection flow.
 * Shared by yg-nighttime-feeding-product and -summary.
 */
;(function () {
  'use strict'

  var STORAGE_PREFIX = 'yg:nighttime-feeding:list:v1:'
  var dsmToastPromise = null

  function safeParse(value, fallback) {
    try {
      return JSON.parse(value)
    } catch (error) {
      return fallback
    }
  }

  function normalizeItem(item) {
    if (!item || !item.productId || !item.variantId) return null
    return {
      productId: String(item.productId),
      variantId: String(item.variantId),
      title: String(item.title || ''),
      variantTitle: String(item.variantTitle || ''),
      options: Array.isArray(item.options) ? item.options : [],
      image: String(item.image || ''),
      price: Number(item.price) || 0,
      compareAtPrice: Number(item.compareAtPrice) || 0,
      currency: String(item.currency || 'USD'),
      quantity: Math.max(1, Math.floor(Number(item.quantity) || 1)),
    }
  }

  function ensureDsmToast(src) {
    if (window.DsmToast) return Promise.resolve(window.DsmToast)
    if (dsmToastPromise) return dsmToastPromise
    if (!src) return Promise.reject(new Error('DSM toast script URL is missing.'))

    dsmToastPromise = new Promise(function (resolve, reject) {
      var script = document.createElement('script')
      script.src = src
      script.async = true
      script.dataset.ygNighttimeFeedingDsm = ''
      script.addEventListener('load', function () {
        if (window.DsmToast) {
          resolve(window.DsmToast)
        } else {
          dsmToastPromise = null
          reject(new Error('DSM toast failed to initialize.'))
        }
      }, { once: true })
      script.addEventListener('error', function () {
        dsmToastPromise = null
        reject(new Error('DSM toast script failed to load.'))
      }, { once: true })
      document.head.appendChild(script)
    })

    return dsmToastPromise
  }

  class YGNighttimeFeedingList extends HTMLElement {
    constructor() {
      super()
      this.storageKey = STORAGE_PREFIX + window.location.pathname
      this.items = []
      this.subscribers = new Set()
      this.registeredProducts = new Map()
      this.activeQuickshopProduct = null
      this.handleStorage = this.handleStorage.bind(this)
      this.handleDocumentClick = this.handleDocumentClick.bind(this)
      this.handleDocumentSubmit = this.handleDocumentSubmit.bind(this)
    }

    connectedCallback() {
      if (this._connected) return
      this._connected = true
      this.hidden = true
      this.readStorage()
      window.addEventListener('storage', this.handleStorage)
      document.addEventListener('click', this.handleDocumentClick, true)
      document.addEventListener('submit', this.handleDocumentSubmit, true)
    }

    disconnectedCallback() {
      window.removeEventListener('storage', this.handleStorage)
      document.removeEventListener('click', this.handleDocumentClick, true)
      document.removeEventListener('submit', this.handleDocumentSubmit, true)
      this.activeQuickshopProduct = null
      this._connected = false
    }

    cloneItems() {
      return this.items.map(function (item) {
        return Object.assign({}, item, {
          options: Array.isArray(item.options) ? item.options.map(function (option) { return Object.assign({}, option) }) : [],
        })
      })
    }

    readStorage() {
      var saved = null
      try {
        saved = safeParse(window.localStorage.getItem(this.storageKey), null)
      } catch (error) {
        console.warn('[yg-nighttime-feeding] Unable to read the saved list.', error)
      }
      var savedItems = saved && saved.version === 1 && Array.isArray(saved.items) ? saved.items : []
      var seen = new Set()
      this.items = savedItems.map(normalizeItem).filter(function (item) {
        if (!item || seen.has(item.variantId)) return false
        seen.add(item.variantId)
        return true
      })
    }

    persist() {
      try {
        window.localStorage.setItem(this.storageKey, JSON.stringify({ version: 1, items: this.items }))
      } catch (error) {
        console.warn('[yg-nighttime-feeding] Unable to persist list.', error)
      }
    }

    notify(source) {
      var snapshot = this.cloneItems()
      this.subscribers.forEach(function (callback) { callback(snapshot, source || 'update') })
      this.dispatchEvent(new CustomEvent('yg:nighttime-feeding:list-changed', {
        bubbles: true,
        detail: { items: snapshot, source: source || 'update', storageKey: this.storageKey },
      }))
    }

    handleStorage(event) {
      if (event.key !== this.storageKey) return
      this.readStorage()
      this.notify('storage')
    }

    handleDocumentClick(event) {
      var quickshopTrigger = event.target.closest?.('[data-action-quickshop]')
      if (quickshopTrigger) {
        this.activeQuickshopProduct = quickshopTrigger.closest('yg-nighttime-feeding-product') || null
        return
      }
      var addButton = event.target.closest?.('.t4s-product-quick-shop [data-atc-form]')
      if (!addButton || !this.activeQuickshopProduct) return
      this.addFromQuickshop(event, addButton.closest('form'))
    }

    handleDocumentSubmit(event) {
      if (!this.activeQuickshopProduct || !event.target.closest?.('.t4s-product-quick-shop')) return
      this.addFromQuickshop(event, event.target)
    }

    addFromQuickshop(event, form) {
      if (!form || !this.activeQuickshopProduct) return
      event.preventDefault()
      event.stopPropagation()
      event.stopImmediatePropagation()

      var variantId = form.querySelector('[name="id"]')?.value
      var quantity = Math.max(1, Math.floor(Number(form.querySelector('[name="quantity"]')?.value) || 1))
      var productElement = this.activeQuickshopProduct
      var variant = productElement.findVariant(variantId)
      if (!variant || !variant.available) return

      if (productElement.addVariant(variant, quantity)) {
        this.activeQuickshopProduct = null
        this.closeQuickshop()
      }
    }

    closeQuickshop() {
      var themeJquery = window.jQuery_T4NT || window.jQuery
      if (themeJquery) {
        themeJquery(document.body).trigger('modalt4s:closed')
        return
      }
      document.body.dispatchEvent(new CustomEvent('modalt4s:closed', { bubbles: true }))
    }

    getItems() {
      return this.cloneItems()
    }

    add(item) {
      var normalized = normalizeItem(item)
      if (!normalized) return false
      var existingItem = this.items.find(function (entry) { return entry.variantId === normalized.variantId })
      if (existingItem) {
        existingItem.quantity += normalized.quantity
      } else {
        this.items.push(normalized)
      }
      this.persist()
      this.notify(existingItem ? 'increase' : 'add')
      return true
    }

    remove(variantId) {
      var target = String(variantId)
      var next = this.items.filter(function (item) { return item.variantId !== target })
      if (next.length === this.items.length) return false
      this.items = next
      this.persist()
      this.notify('remove')
      return true
    }

    subscribe(callback) {
      if (typeof callback !== 'function') return function () {}
      this.subscribers.add(callback)
      callback(this.cloneItems(), 'subscribe')
      return function () { this.subscribers.delete(callback) }.bind(this)
    }

    registerProduct(productId, variants) {
      if (!productId) return
      var validVariants = new Set()
      ;(Array.isArray(variants) ? variants : []).forEach(function (variant) {
        if (variant && variant.available && variant.variantId) validVariants.add(String(variant.variantId))
      })
      this.registeredProducts.set(String(productId), validVariants)
    }

    pruneInvalid() {
      var next = this.items.filter(function (item) {
        var variants = this.registeredProducts.get(item.productId)
        return variants && variants.has(item.variantId)
      }.bind(this))
      if (next.length === this.items.length) return
      this.items = next
      this.persist()
      this.notify('prune')
    }

    formatMoney(cents, currency) {
      var formattedMoney
      if (window.T4SThemeSP && window.T4SThemeSP.Currency && typeof window.T4SThemeSP.Currency.formatMoney === 'function') {
        formattedMoney = window.T4SThemeSP.Currency.formatMoney(Number(cents) || 0)
        return this.removeCurrencyCode(formattedMoney)
      }
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        formattedMoney = window.Shopify.formatMoney(Number(cents) || 0)
        return this.removeCurrencyCode(formattedMoney)
      }
      try {
        formattedMoney = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
          style: 'currency',
          currency: currency || 'USD',
        }).format((Number(cents) || 0) / 100)
        return this.removeCurrencyCode(formattedMoney)
      } catch (error) {
        return '$' + ((Number(cents) || 0) / 100).toFixed(2)
      }
    }

    removeCurrencyCode(value) {
      return String(value || '')
        .replace(/^[A-Z]{3}[\s\u00a0]+/, '')
        .replace(/[\s\u00a0]+[A-Z]{3}$/, '')
        .trim()
    }
  }

  function getListStore() {
    var store = document.querySelector('yg-nighttime-feeding-list[data-yg-ntf-list-store]')
    if (store) return store
    store = document.createElement('yg-nighttime-feeding-list')
    store.setAttribute('data-yg-ntf-list-store', '')
    ;(document.body || document.documentElement).appendChild(store)
    return store
  }

  class YGNighttimeFeedingProduct extends HTMLElement {
    connectedCallback() {
      if (this._connected) return
      this._connected = true
      this.abortController = new AbortController()
      this.data = safeParse(this.querySelector('[data-yg-ntf-product-data]')?.textContent || '{}', {})
      this.button = this.querySelector('[data-yg-ntf-add-button]')
      this.dsmComponentsSrc = this.dataset.dsmComponentsSrc || ''
      this.variants = Array.isArray(this.data.variants) ? this.data.variants : []
      var preferredVariant = this.findVariant(this.data.defaultVariantId)
      this.defaultVariant = preferredVariant && preferredVariant.available
        ? preferredVariant
        : this.variants.find(function (variant) { return variant.available })

      this.listStore = getListStore()
      this.listStore.registerProduct(this.data.productId, this.variants)
      this.bindAddButton()
      this.bindFlip()
      this.updateButton()
    }

    disconnectedCallback() {
      this.abortController?.abort()
      window.clearTimeout(this.flipFocusTimer)
      this.flipTransitionId = (this.flipTransitionId || 0) + 1
      this._connected = false
    }

    findVariant(variantId) {
      var target = String(variantId || '')
      return this.variants.find(function (variant) { return String(variant.variantId) === target })
    }

    bindAddButton() {
      if (!this.button) return
      this.button.addEventListener('click', function (event) {
        if (this.button.disabled || this.button.getAttribute('aria-disabled') === 'true') {
          event.preventDefault()
          event.stopPropagation()
          return
        }
        if (this.button.hasAttribute('data-action-quickshop')) return
        event.preventDefault()
        this.addVariant(this.defaultVariant)
      }.bind(this), { signal: this.abortController.signal })
    }

    bindFlip() {
      var flip = this.querySelector('[data-yg-ntf-flip]')
      if (!flip) return
      var inner = flip.querySelector('.yg-ntf-product__flip-inner')
      var front = flip.querySelector('[data-yg-ntf-flip-front]')
      var back = flip.querySelector('[data-yg-ntf-flip-back]')
      var setFlip = function (isFlipped) {
        var currentlyFlipped = flip.getAttribute('data-flipped') === 'true'
        if (currentlyFlipped === isFlipped) return

        this.flipTransitionId = (this.flipTransitionId || 0) + 1
        var transitionId = this.flipTransitionId
        window.clearTimeout(this.flipFocusTimer)

        flip.classList.toggle('is-flipped', isFlipped)
        flip.setAttribute('data-flipped', isFlipped ? 'true' : 'false')
        if (front) front.setAttribute('aria-hidden', isFlipped ? 'true' : 'false')
        if (back) back.setAttribute('aria-hidden', isFlipped ? 'false' : 'true')
        if (front) front.tabIndex = isFlipped ? -1 : 0
        if (back) back.tabIndex = isFlipped ? 0 : -1
        var activeFace = isFlipped ? back : front
        var finishFlip = function () {
          if (transitionId !== this.flipTransitionId) return
          window.clearTimeout(this.flipFocusTimer)
          activeFace?.querySelectorAll('motion-element').forEach(function (element) {
            if (typeof element.refreshAnimation === 'function') element.refreshAnimation()
          })
          activeFace?.focus({ preventScroll: true })
        }.bind(this)

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !inner) {
          finishFlip()
          return
        }

        inner.addEventListener('transitionend', function (event) {
          if (event.target === inner && event.propertyName === 'transform') finishFlip()
        }, { once: true, signal: this.abortController.signal })
        this.flipFocusTimer = window.setTimeout(finishFlip, 900)
      }.bind(this)

      flip.addEventListener('click', function (event) {
        var face = event.target.closest('[data-yg-ntf-flip-front], [data-yg-ntf-flip-back]')
        if (!face || !flip.contains(face)) return
        setFlip(flip.getAttribute('data-flipped') !== 'true')
      }, { signal: this.abortController.signal })
    }

    addVariant(variant, quantity) {
      if (!variant || !variant.available) return false
      var added = this.listStore.add({
        productId: this.data.productId,
        variantId: variant.variantId,
        title: this.data.title,
        variantTitle: variant.title,
        options: variant.options,
        image: variant.image || this.data.image,
        price: variant.price,
        compareAtPrice: variant.compareAtPrice,
        currency: this.data.currency,
        quantity: Math.max(1, Math.floor(Number(quantity) || 1)),
      })
      if (added) this.applyVariant(variant)
      if (added) this.showToast(this.dataset.addedToast)
      return added
    }

    applyVariant(variant) {
      var price = this.querySelector('[data-yg-ntf-product-price]')
      var comparePrice = this.querySelector('[data-yg-ntf-product-compare-price]')
      if (price) {
        price.textContent = variant.formattedPrice || this.listStore.formatMoney(variant.price, this.data.currency)
      }
      if (comparePrice) {
        comparePrice.textContent = variant.formattedCompareAtPrice || this.listStore.formatMoney(variant.compareAtPrice, this.data.currency)
        comparePrice.hidden = !(variant.compareAtPrice > variant.price)
      }
    }

    showToast(message) {
      if (!message) return
      ensureDsmToast(this.dsmComponentsSrc).then(function (toast) {
        toast.show({
          message: message,
          tone: 'success',
          placement: 'bottom-center',
          offset: 40,
          grouping: true,
        })
      }).catch(function (error) {
        console.error('[yg-nighttime-feeding] Unable to show the add-to-list notification.', error)
      })
    }

    updateButton() {
      if (!this.button) return
      var label = this.button.querySelector('.dsm-btn__label')
      var plus = this.button.querySelector('[data-yg-ntf-plus-icon]')
      var buttonLabel = this.defaultVariant ? this.dataset.addLabel : this.dataset.soldOutLabel
      this.button.disabled = !this.defaultVariant
      this.button.classList.toggle('dsm-btn--disabled', this.button.disabled)
      this.button.setAttribute('aria-disabled', this.button.disabled ? 'true' : 'false')
      this.button.setAttribute('aria-label', buttonLabel)
      if (label) label.textContent = buttonLabel
      if (plus) plus.hidden = !this.defaultVariant
      if (this.defaultVariant) this.applyVariant(this.defaultVariant)
    }
  }

  class YGNighttimeFeedingSummary extends HTMLElement {
    constructor() {
      super()
      this.handlePageShow = this.handlePageShow.bind(this)
    }

    connectedCallback() {
      if (this._connected) return
      this._connected = true
      this.abortController = new AbortController()
      this.config = safeParse(this.querySelector('[data-yg-ntf-summary-config]')?.textContent || '{}', {})
      this.itemsContainer = this.querySelector('[data-yg-ntf-summary-items]')
      this.description = this.querySelector('[data-yg-ntf-summary-description]')
      this.emptyDescription = this.querySelector('[data-yg-ntf-summary-empty-description]')
      this.emptyState = this.querySelector('[data-yg-ntf-summary-empty-state]')
      this.emptyCount = this.querySelector('[data-yg-ntf-summary-empty-count]')
      this.recommendations = this.querySelector('[data-yg-ntf-summary-recommendations]')
      this.footer = this.querySelector('[data-yg-ntf-summary-footer]')
      this.count = this.querySelector('[data-yg-ntf-summary-count]')
      this.total = this.querySelector('[data-yg-ntf-summary-total]')
      this.compareTotal = this.querySelector('[data-yg-ntf-summary-compare-total]')
      this.buyButton = this.querySelector('[data-yg-ntf-summary-buy]')
      this.error = this.querySelector('[data-yg-ntf-summary-error]')
      this.dismissedRecommendationIds = new Set()
      this.loading = false
      this.createLoadingSpinner()
      this.listStore = getListStore()
      this.bindEvents()
      this.unsubscribe = this.listStore.subscribe(this.render.bind(this))
    }

    disconnectedCallback() {
      this.abortController?.abort()
      window.clearTimeout(this.recommendationQuickshopTimer)
      if (this.unsubscribe) this.unsubscribe()
      this._connected = false
    }

    bindEvents() {
      this.addEventListener('click', function (event) {
        var removeButton = event.target.closest('[data-yg-ntf-remove]')
        if (removeButton && this.contains(removeButton)) {
          this.listStore.remove(removeButton.dataset.variantId)
          return
        }
        var recommendationClose = event.target.closest('[data-yg-ntf-recommendation-close]')
        if (recommendationClose && this.contains(recommendationClose)) {
          this.dismissedRecommendationIds.add(String(recommendationClose.dataset.productId || ''))
          this.renderRecommendations(this.currentItems || [])
          return
        }
        var recommendationButton = event.target.closest('[data-yg-ntf-recommendation-add]')
        if (recommendationButton && this.contains(recommendationButton)) {
          event.preventDefault()
          event.stopPropagation()

          var productId = recommendationButton.dataset.productId
          var recommendation = this.getRecommendations(this.currentItems || []).find(function (item) {
            return item.productId === productId
          })
          var sourceButton = recommendation?.sourceElement?.querySelector('[data-yg-ntf-add-button]')
          if (!sourceButton || sourceButton.disabled || sourceButton.getAttribute('aria-disabled') === 'true') return

          // The theme opens cached Quick Shop markup synchronously. Triggering the
          // source button inside this click would leave the recommendation click
          // bubbling after the modal opens, so the popup treats it as an outside
          // click and immediately closes. Wait until this event has finished.
          window.clearTimeout(this.recommendationQuickshopTimer)
          this.recommendationQuickshopTimer = window.setTimeout(function () {
            if (sourceButton.isConnected) sourceButton.click()
          }, 0)
        }
      }.bind(this), { signal: this.abortController.signal })
      this.buyButton?.addEventListener('click', this.checkout.bind(this), { signal: this.abortController.signal })
      window.addEventListener('pageshow', this.handlePageShow, { signal: this.abortController.signal })
    }

    handlePageShow() {
      if (!this.loading) return
      this.loading = false
      this.updateBuyButton()
    }

    createLoadingSpinner() {
      if (!this.buyButton) return
      this.loadingSpinner = this.buyButton.querySelector('[data-yg-ntf-summary-spinner]')
      if (this.loadingSpinner) return
      this.loadingSpinner = document.createElement('span')
      this.loadingSpinner.className = 'yg-ntf-summary__loading-spinner'
      this.loadingSpinner.setAttribute('data-yg-ntf-summary-spinner', '')
      this.loadingSpinner.setAttribute('aria-hidden', 'true')
      this.loadingSpinner.hidden = true
      this.loadingSpinner.innerHTML = '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" focusable="false">' +
        '<circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-dasharray="42 16"/>' +
        '</svg>'
      this.buyButton.prepend(this.loadingSpinner)
    }

    escapeHtml(value) {
      var node = document.createElement('div')
      node.textContent = String(value || '')
      return node.innerHTML
    }

    getRecommendations(list) {
      var seenProducts = new Set()
      var selectedProductIds = new Set((Array.isArray(list) ? list : []).map(function (item) {
        return String(item.productId || '')
      }))
      var configuredRecommendations = this.hasAttribute('data-yg-ntf-configured-recommendations')
      // New summaries own their recommendation controllers. Legacy summaries
      // continue discovering visible product sections across the page.
      var productElements = configuredRecommendations
        ? this.querySelectorAll('[data-yg-ntf-recommendation-sources] yg-nighttime-feeding-product')
        : Array.from(document.querySelectorAll('yg-nighttime-feeding-product')).filter(function (productElement) {
            return !productElement.closest('[data-yg-ntf-recommendation-sources]')
          })
      return Array.from(productElements).map(function (productElement) {
        var data = productElement.data || safeParse(productElement.querySelector('[data-yg-ntf-product-data]')?.textContent || '{}', {})
        var productId = String(data.productId || '')
        if (!productId || seenProducts.has(productId) || selectedProductIds.has(productId) || this.dismissedRecommendationIds.has(productId)) return null
        seenProducts.add(productId)
        var variants = Array.isArray(data.variants) ? data.variants : []
        var defaultVariant = productElement.defaultVariant || variants.find(function (variant) { return variant.available })
        return {
          sourceElement: productElement,
          productId: productId,
          title: data.title || '',
          description: data.recommendationDescription || '',
          image: data.recommendationImage || data.image || defaultVariant?.image || '',
          mobileImage: configuredRecommendations ? data.recommendationImageMobile || data.image || data.recommendationImage || defaultVariant?.image || '' : '',
          tabletImage: data.recommendationImageMobile || data.image || data.recommendationImage || defaultVariant?.image || '',
          imageAlt: data.recommendationImageAlt || data.title || '',
          available: Boolean(defaultVariant),
          buttonLabel: configuredRecommendations
            ? (defaultVariant ? this.dataset.addLabel : this.dataset.soldOutLabel)
            : (defaultVariant ? productElement.dataset.addLabel : productElement.dataset.soldOutLabel),
          buttonAriaLabel: defaultVariant ? productElement.dataset.addLabel : productElement.dataset.soldOutLabel,
          editorBlock: configuredRecommendations ? productElement.getAttribute('data-shopify-editor-block') : '',
        }
      }.bind(this)).filter(Boolean).slice(0, 3)
    }

    recommendationHtml(item, index, animate) {
      var imageHtml = item.image
        ? '<div class="yg-ntf-summary__recommendation-image"><picture>' +
            (item.mobileImage ? '<source media="(max-width: 768px)" srcset="' + this.escapeHtml(item.mobileImage) + '">' : '') +
            (item.tabletImage ? '<source media="(min-width: 769px) and (max-width: 1024px)" srcset="' + this.escapeHtml(item.tabletImage) + '">' : '') +
            '<img src="' + this.escapeHtml(item.image) + '" alt="' + this.escapeHtml(item.imageAlt) + '" loading="lazy">' +
          '</picture></div>'
        : ''
      var disabledAttributes = item.available ? '' : ' disabled aria-disabled="true"'
      var disabledClass = item.available ? '' : ' dsm-btn--disabled'
      var closeIcon = '<svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
        '<path d="M11.7298 3.56297C11.9251 3.36771 12.2416 3.36771 12.4369 3.56297C12.6319 3.75824 12.6321 4.0748 12.4369 4.27L8.70704 7.99983L12.4369 11.7297C12.6319 11.9249 12.6321 12.2415 12.4369 12.4367C12.2417 12.6319 11.9251 12.6318 11.7298 12.4367L8 8.70686L4.27018 12.4367C4.07498 12.6319 3.75842 12.6318 3.56314 12.4367C3.36788 12.2414 3.36788 11.9249 3.56314 11.7297L7.29297 7.99983L3.56314 4.27C3.36788 4.07474 3.36788 3.75823 3.56314 3.56297C3.75841 3.36771 4.07492 3.36771 4.27018 3.56297L8 7.29279L11.7298 3.56297Z" fill="currentColor"/>' +
        '</svg>'
      var editorAttribute = item.editorBlock ? ' data-shopify-editor-block="' + this.escapeHtml(item.editorBlock) + '"' : ''
      var recommendation = '<article class="yg-ntf-summary__recommendation"' + editorAttribute + '>' +
          imageHtml +
          '<div class="yg-ntf-summary__recommendation-details">' +
            '<h3 class="yg-ntf-summary__recommendation-title">' + this.escapeHtml(item.title) + '</h3>' +
            '<p class="yg-ntf-summary__recommendation-description">' + this.escapeHtml(item.description) + '</p>' +
            '<button class="dsm-btn dsm-btn--theme dsm-btn--large yg-ntf-summary__recommendation-button' + disabledClass + '" type="button" data-yg-ntf-recommendation-add data-product-id="' + this.escapeHtml(item.productId) + '" aria-label="' + this.escapeHtml(item.buttonAriaLabel || item.buttonLabel) + '"' + disabledAttributes + '>' +
              '<span class="dsm-btn__label">' + this.escapeHtml(item.buttonLabel) + '</span>' +
            '</button>' +
          '</div>' +
          '<button class="yg-ntf-summary__recommendation-close" type="button" data-yg-ntf-recommendation-close data-product-id="' + this.escapeHtml(item.productId) + '" aria-label="' + this.escapeHtml(this.config.recommendationCloseLabel || 'Hide recommendation') + '">' + closeIcon + '</button>' +
        '</article>'
      if (!animate) return recommendation
      return '<motion-element data-motion="fade-up-sm" data-motion-delay="' + (220 + Math.min(index * 80, 160)) + '">' + recommendation + '</motion-element>'
    }

    renderRecommendations(list, animate) {
      if (!this.recommendations) return
      var recommendations = this.getRecommendations(list)
      this.recommendations.innerHTML = recommendations.map(function (item, index) {
        return this.recommendationHtml(item, index, animate)
      }.bind(this)).join('')
      this.recommendations.hidden = recommendations.length === 0
    }

    itemHtml(item, index, animate) {
      var quantity = Math.max(1, Math.floor(Number(item.quantity) || 1))
      var optionMeta = (item.options || []).filter(function (option) {
        return option && option.value && option.value !== 'Default Title'
      }).map(function (option) {
        return this.escapeHtml(option.name) + ': ' + this.escapeHtml(option.value)
      }.bind(this))
      if (quantity > 1) optionMeta.push(this.escapeHtml(this.config.quantityLabel || 'Qty') + ': ' + quantity)
      var itemMeta = optionMeta.map(function (meta) {
        return '<span class="yg-ntf-summary__item-option">' + meta + '</span>'
      }).join(' ')
      var linePrice = (Number(item.price) || 0) * quantity
      var removeIcon = '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
        '<path d="M14.9697 7.96973C15.2626 7.67683 15.7374 7.67683 16.0303 7.96973C16.3231 8.26262 16.3232 8.73739 16.0303 9.03027L13.0605 12L16.0303 14.9697C16.3231 15.2626 16.3232 15.7374 16.0303 16.0303C15.7374 16.3231 15.2626 16.3231 14.9697 16.0303L12 13.0605L9.03027 16.0303C8.73739 16.3231 8.26261 16.3231 7.96973 16.0303C7.67684 15.7374 7.67686 15.2626 7.96973 14.9697L10.9395 12L7.96973 9.03027C7.67684 8.73739 7.67686 8.26262 7.96973 7.96973C8.26262 7.67683 8.73738 7.67683 9.03027 7.96973L12 10.9395L14.9697 7.96973Z" fill="currentColor"/>' +
        '<path d="M12 1.25C17.9371 1.25 22.75 6.06294 22.75 12C22.75 17.9371 17.9371 22.75 12 22.75C6.06294 22.75 1.25 17.9371 1.25 12C1.25 6.06294 6.06294 1.25 12 1.25ZM12 2.75C6.89137 2.75 2.75 6.89137 2.75 12C2.75 17.1086 6.89137 21.25 12 21.25C17.1086 21.25 21.25 17.1086 21.25 12C21.25 6.89137 17.1086 2.75 12 2.75Z" fill="currentColor"/>' +
        '</svg>'
      var summaryItem = '<article class="yg-ntf-summary__item">' +
          '<div class="yg-ntf-summary__item-main">' +
            '<div class="yg-ntf-summary__item-image"><img src="' + this.escapeHtml(item.image) + '" alt="' + this.escapeHtml(item.title) + '" loading="lazy"></div>' +
            '<div class="yg-ntf-summary__item-copy">' +
              '<h3 class="yg-ntf-summary__item-title">' + this.escapeHtml(item.title) + '</h3>' +
              '<p class="yg-ntf-summary__item-options">' + itemMeta + '</p>' +
              '<span class="yg-ntf-summary__item-price yg-ntf-summary__item-price--mobile">' + this.listStore.formatMoney(linePrice, item.currency) + '</span>' +
            '</div>' +
          '</div>' +
          '<div class="yg-ntf-summary__item-actions">' +
            '<span class="yg-ntf-summary__item-price yg-ntf-summary__item-price--desktop">' + this.listStore.formatMoney(linePrice, item.currency) + '</span>' +
            '<button class="dsm-btn dsm-btn--text dsm-btn--small dsm-btn--square yg-ntf-summary__remove" type="button" data-yg-ntf-remove data-variant-id="' + this.escapeHtml(item.variantId) + '" aria-label="' + this.escapeHtml(this.config.removeLabel) + '">' + removeIcon + '</button>' +
          '</div>' +
        '</article>'
      if (!animate) return summaryItem
      return '<motion-element data-motion="fade-up-sm" data-motion-delay="' + Math.min(index * 80, 400) + '">' + summaryItem + '</motion-element>'
    }

    render(list, source) {
      var isEmpty = list.length === 0
      // Keep the initial reveal, but let user-driven list updates appear immediately.
      var animate = source === 'subscribe'
      this.classList.toggle('is-empty', isEmpty)
      var currency = list[0]?.currency || this.config.currency || 'USD'
      var selectedCount = list.reduce(function (sum, item) {
        return sum + Math.max(1, Math.floor(Number(item.quantity) || 1))
      }, 0)
      var total = list.reduce(function (sum, item) {
        var quantity = Math.max(1, Math.floor(Number(item.quantity) || 1))
        return sum + ((Number(item.price) || 0) * quantity)
      }, 0)
      var compareTotal = list.reduce(function (sum, item) {
        var compare = Number(item.compareAtPrice) || 0
        var price = Number(item.price) || 0
        var quantity = Math.max(1, Math.floor(Number(item.quantity) || 1))
        return sum + ((compare > price ? compare : price) * quantity)
      }, 0)
      if (this.itemsContainer) {
        this.itemsContainer.innerHTML = list.map(function (item, index) {
          return this.itemHtml(item, index, animate)
        }.bind(this)).join('')
        this.itemsContainer.hidden = isEmpty
      }
      if (this.description) this.description.hidden = isEmpty
      if (this.emptyDescription) this.emptyDescription.hidden = !isEmpty
      if (this.emptyState) this.emptyState.hidden = !isEmpty
      if (this.footer) this.footer.hidden = isEmpty
      this.querySelectorAll('[data-yg-ntf-summary-empty-count]').forEach(function (element) {
        element.textContent = String(this.config.selectedLabel || '{count} item(s) selected').replace('{count}', 0)
      }.bind(this))
      this.renderRecommendations(list, animate)
      if (this.count) this.count.textContent = String(this.config.selectedLabel || '{count} item(s) selected').replace('{count}', selectedCount)
      if (this.total) this.total.textContent = String(this.config.totalLabel || 'Total Price: {price}').replace('{price}', this.listStore.formatMoney(total, currency))
      if (this.compareTotal) {
        this.compareTotal.textContent = this.listStore.formatMoney(compareTotal, currency)
        this.compareTotal.hidden = !(compareTotal > total)
      }
      this.currentItems = list
      this.updateBuyButton()
    }

    updateBuyButton() {
      if (!this.buyButton) return
      var hasItems = Array.isArray(this.currentItems) && this.currentItems.length > 0
      var hasToken = Boolean(String(this.config.token || '').trim())
      this.buyButton.disabled = this.loading || !hasItems || !hasToken
      this.buyButton.classList.toggle('dsm-btn--disabled', this.buyButton.disabled)
      this.buyButton.classList.toggle('is-loading', this.loading)
      this.buyButton.setAttribute('aria-disabled', this.buyButton.disabled ? 'true' : 'false')
      this.buyButton.setAttribute('aria-busy', this.loading ? 'true' : 'false')
      var label = this.buyButton.querySelector('.dsm-btn__label')
      if (label) {
        label.textContent = this.config.buyLabel
        label.hidden = this.loading
      }
      if (this.loadingSpinner) this.loadingSpinner.hidden = !this.loading
      if (!hasItems) this.clearError()
      else if (!hasToken) this.showError(this.config.missingTokenText)
      else if (!this.loading) this.clearError()
    }

    showError(message) {
      if (!this.error) return
      this.error.textContent = message || this.config.errorText
      this.error.hidden = false
    }

    clearError() {
      if (!this.error) return
      this.error.textContent = ''
      this.error.hidden = true
    }

    async checkout() {
      if (this.loading || !this.currentItems?.length || !String(this.config.token || '').trim()) return
      this.loading = true
      this.clearError()
      this.updateBuyButton()
      try {
        var response = await fetch('https://' + this.config.shopDomain + '/api/2026-01/graphql.json', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': String(this.config.token).trim(),
          },
          body: JSON.stringify({
            query: 'mutation NighttimeFeedingCartCreate($input: CartInput!) { cartCreate(input: $input) { cart { id checkoutUrl } userErrors { field message } } }',
            variables: {
              input: {
                lines: this.currentItems.map(function (item) {
                  return {
                    quantity: Math.max(1, Math.floor(Number(item.quantity) || 1)),
                    merchandiseId: 'gid://shopify/ProductVariant/' + item.variantId,
                  }
                }),
              },
            },
          }),
        })
        var result = await response.json()
        if (!response.ok) throw new Error(this.config.errorText)
        if (Array.isArray(result.errors) && result.errors.length) throw new Error(result.errors.map(function (error) { return error.message }).join('; '))
        var payload = result.data?.cartCreate
        if (payload?.userErrors?.length) throw new Error(payload.userErrors.map(function (error) { return error.message }).join('; '))
        if (!payload?.cart?.checkoutUrl) throw new Error(this.config.errorText)
        window.location.assign(payload.cart.checkoutUrl)
      } catch (error) {
        console.error('[yg-nighttime-feeding] Checkout failed.', error)
        this.showError(error.message || this.config.errorText)
        this.loading = false
        this.updateBuyButton()
      }
    }
  }

  if (!customElements.get('yg-nighttime-feeding-list')) {
    customElements.define('yg-nighttime-feeding-list', YGNighttimeFeedingList)
  }
  if (!customElements.get('yg-nighttime-feeding-product')) {
    customElements.define('yg-nighttime-feeding-product', YGNighttimeFeedingProduct)
  }
  if (!customElements.get('yg-nighttime-feeding-summary')) {
    customElements.define('yg-nighttime-feeding-summary', YGNighttimeFeedingSummary)
  }

  var pruneSavedList = function () {
    window.setTimeout(function () { getListStore().pruneInvalid() }, 0)
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', pruneSavedList, { once: true })
  else pruneSavedList()
})()
